import * as THREE from 'three';
import { EDirection, EEntityType, EOperateMode, ESceneMode, EToolType, IOperateInfo, ISelected } from './types';
import { modelManager } from './manager/entityManager';
import { globalStores } from './stores';
import editorEvent, { EditorEventName, EditorEventType } from './event/editorEvent';
import { ControllerManager } from './controller';
import { EditParams } from './controller/editParams';
import { clientPosToWorldPos, worldPosToClientPos } from './utils';
import { loadSceneScheme, saveSceneScheme } from './scheme';
import { CursorController } from './controller/cursorController';
import { capture2dAsync } from './utils/captureScene';
import { VisibleController } from './controller/visible';
import { KeyboardController } from './controller/keyboardController';
import GroundModel3d from './entities/3d/groundModel';
import GroundModel2d from './entities/2d/groundModel';
import { ILibraryMaterial, ILibraryModel } from '@/service/library/types';
import { IScheme } from '@/service/scheme/types';
import { EShortcutId } from '@/editor/controller/keyboardController/config';
import { SelectedRulerController } from './controller/selectedRulerController';
import { ContextMenuController } from './controller/contextMenuController';
import { FLAGS } from '@/flags';

const OPERATE_MODE_HANDLE_ESCAPE_INSIDE: EOperateMode[] = [
  EOperateMode.DrawWall,
  EOperateMode.DrawRectWall,
  EOperateMode.DrawArcWall,
  EOperateMode.DrawSeparateWall,
  EOperateMode.DrawArcSeparateWall
] as const;

export class Editor {
  private static _instance: Editor;
  private _sceneInfo = globalStores.editorStateStore.sceneInfo;
  private _requestAnimationFrameId: number | null = null;
  private _eventBus = editorEvent;
  private _controllerManager: ControllerManager;
  private _cursorController: CursorController;
  private _keyboardController: KeyboardController;

  private _isSchemeLoading = false;

  private _groundModel2d: GroundModel2d;
  private _groundModel3d: GroundModel3d;

  editParams: EditParams;
  contextMenuController: ContextMenuController;
  visibleController: VisibleController;

  modelManager = modelManager;
  selectedRulerController: SelectedRulerController;

  get sceneMode() {
    return globalStores.sceneMode;
  }

  setOperateInfo(operateInfo: IOperateInfo) {
    globalStores.operateInfo = operateInfo;
  }

  get operateInfo() {
    return globalStores.operateInfo;
  }

  set operateInfo(operateInfo: IOperateInfo) {
    globalStores.operateInfo = operateInfo;
  }

  get sceneContainer() {
    return this._sceneInfo.canvasContainer;
  }

  get keyboardController() {
    return this._keyboardController;
  }

  private constructor() {
    this.editParams = new EditParams();
    this._controllerManager = new ControllerManager(globalStores);
    this.contextMenuController = new ContextMenuController();
    this._cursorController = new CursorController();
    this._keyboardController = new KeyboardController(globalStores);
    this.visibleController = new VisibleController();
    this.selectedRulerController = new SelectedRulerController();

    // 初始化键盘控制器
    this._registerShortcuts();

    // 添加2d地面网格
    this._groundModel2d = new GroundModel2d();
    this._groundModel2d.createModel();

    if (FLAGS.FEATURES_DEV.ENABLE_AXIS) {
      const axesHelper = new THREE.AxesHelper(100);
      globalStores.editorStateStore.sceneInfo.scene2d.scene.add(axesHelper);
    }

    // 添加3d地面网格
    this._groundModel3d = new GroundModel3d('ground');
    this._groundModel3d.createModel();

    if (FLAGS.FEATURES_DEV.ENABLE_AXIS) {
      const axesHelper = new THREE.AxesHelper(100);
      globalStores.editorStateStore.sceneInfo.scene3d.scene.add(axesHelper);
    }

    editorEvent.on(EditorEventName.SCENE_MODE_CHANGE, () => {
      // 先暂停渲染
      this.stopAnimate();

      // 重新开始渲染
      this.startAnimate();
    });

    editorEvent.on(EditorEventName.ENTITY_SELECTED, (selected: ISelected) =>
      this._registerRemoveEntityShortcuts(selected)
    );
    editorEvent.on(EditorEventName.ENTITY_CLEAR, () => this._registerRemoveEntityShortcuts());
  }

  static getSingleton() {
    if (!Editor._instance) {
      Editor._instance = new Editor();
      Editor._instance.startAnimate();
    }
    return Editor._instance;
  }

  /**
   * 根据canvas父组件尺寸更新相机和渲染器尺寸
   */
  handleResize = () => {
    const canvasContainer = this._sceneInfo.canvasContainer;
    if (!canvasContainer) return;

    // 设置canvas元素的宽高
    this._sceneInfo.canvasElement.style.width = canvasContainer.clientWidth + 'px';
    this._sceneInfo.canvasElement.style.height = canvasContainer.clientHeight + 'px';

    editorEvent.emit(EditorEventName.RESIZE);
  };

  undo = () => {
    // 撤销前先同步控制器里延时提交的数据（如方向键微调 entity）
    this._controllerManager.flushSync();
    globalStores.history.undo();
  };

  redo = () => {
    this._controllerManager.flushSync();
    globalStores.history.redo();
  };

  getZoom = () => {
    switch (this.sceneMode) {
      case ESceneMode.scene2d:
        return globalStores.scene2dZoom;
      case ESceneMode.scene3d:
        return globalStores.scene3dZoom;
    }
  };

  setZoom = (zoom: number) => {
    switch (this.sceneMode) {
      case ESceneMode.scene2d:
        globalStores.scene2dZoom = zoom;
        break;
      case ESceneMode.scene3d:
        globalStores.scene3dZoom = zoom;
        break;
    }
  };

  public fitToView = () => {
    this._controllerManager.fitToView();
  };

  clientPosToScenePos(x: number, y: number, z: number) {
    const camera =
      this.sceneMode === ESceneMode.scene2d ? this._sceneInfo.scene2d.camera : this._sceneInfo.scene3d.camera;
    return clientPosToWorldPos({ x, y, z }, camera, this._sceneInfo.renderer);
  }

  scenePosToClientPos(x: number, y: number, z: number) {
    const camera =
      this.sceneMode === ESceneMode.scene2d ? this._sceneInfo.scene2d.camera : this._sceneInfo.scene3d.camera;
    const renderer = this._sceneInfo.renderer;
    return worldPosToClientPos({ x, y, z }, camera, renderer);
  }

  getSceneScheme() {
    return JSON.stringify(saveSceneScheme());
  }

  async loadScheme(data?: IScheme) {
    if (!data || !data.schemeJson || this._isSchemeLoading) return;

    globalStores.history.clear(); // 清空数据并停止记录
    this._isSchemeLoading = true;

    try {
      await loadSceneScheme(data.schemeJson);
      this._controllerManager.fitToView();
    } finally {
      // 失败也要恢复：history 停在 clear 状态会让撤销栈静默失效，
      // 标记不复位则此后任何 loadScheme 都被开头的守卫挡掉
      globalStores.history.init();
      this._isSchemeLoading = false;
    }
  }

  loadModels(models: ILibraryModel[]) {
    globalStores.modelStore.loadModels(models || []);
  }

  loadMaterials(materials: ILibraryMaterial[]) {
    globalStores.materialStore.loadMaterials(materials || []);
  }

  /** 从 store 数据离屏重建场景的截图，直接产出可上传的 PNG 二进制 */
  async capture2dScene(): Promise<Blob | null> {
    return capture2dAsync({ renderer: this._sceneInfo.renderer });
  }

  public escape() {
    // 处于画布交互状态时，不响应 escape 操作
    if (
      globalStores.editorStateStore.moving ||
      globalStores.editorStateStore.dragging ||
      globalStores.editorStateStore.rotating ||
      globalStores.editorStateStore.zooming
    ) {
      return;
    }

    if (globalStores.operateInfo.mode === EOperateMode.Alignment) {
      globalStores.alignOperation.toggleAlignModeEnabled(false);
      return;
    }

    if (globalStores.operateInfo.mode === EOperateMode.EMPTY && globalStores.editorStateStore.selected.entityId) {
      globalStores.editorStateStore.clearSelected();
    }

    if (!OPERATE_MODE_HANDLE_ESCAPE_INSIDE.includes(globalStores.operateInfo.mode)) {
      globalStores.operateInfo = { mode: EOperateMode.EMPTY, toolType: EToolType.empty };
    }
  }

  private _registerShortcuts() {
    // 取消/退出
    this._keyboardController.register({
      id: EShortcutId.ESCAPE,
      callback: () => this.escape()
    });

    if (FLAGS.FEATURES.ENABLE_SHORTCUT_V1) {
      // 适应视图
      this._keyboardController.register({
        id: EShortcutId.FIT_TO_VIEW,
        callback: () => this.fitToView()
      });

      // 绘制墙体
      this._keyboardController.register({
        id: EShortcutId.DRAW_WALL,
        callback: () => {
          globalStores.operateInfo = {
            mode: EOperateMode.DrawWall,
            toolType: EToolType.lineWall
          };
        }
      });

      // 绘制矩形墙体
      this._keyboardController.register({
        id: EShortcutId.DRAW_RECTANGLE_WALL,
        callback: () => {
          globalStores.operateInfo = {
            mode: EOperateMode.DrawRectWall,
            toolType: EToolType.rectWall
          };
        }
      });

      if (FLAGS.FEATURES.ENABLE_ARC_WALL) {
        this._keyboardController.register({
          id: EShortcutId.DRAW_ARC_WALL,
          callback: () => {
            globalStores.operateInfo = {
              mode: EOperateMode.DrawArcWall,
              toolType: EToolType.arcWall
            };
          }
        });
      }

      this._keyboardController.register({
        id: EShortcutId.MOVE_ENTITY_UP,
        callback: () => {
          this._controllerManager.moveEntityByDirection(EDirection.UP);
        }
      });
      this._keyboardController.register({
        id: EShortcutId.MOVE_ENTITY_DOWN,
        callback: () => {
          this._controllerManager.moveEntityByDirection(EDirection.DOWN);
        }
      });
      this._keyboardController.register({
        id: EShortcutId.MOVE_ENTITY_LEFT,
        callback: () => {
          this._controllerManager.moveEntityByDirection(EDirection.LEFT);
        }
      });
      this._keyboardController.register({
        id: EShortcutId.MOVE_ENTITY_RIGHT,
        callback: () => {
          this._controllerManager.moveEntityByDirection(EDirection.RIGHT);
        }
      });
    }
  }

  private _registerRemoveEntityShortcuts(selected?: ISelected) {
    // 仅标注支持快捷键删除；其余类型（含空选中、墙体/家具等）都需取消注册，
    // 否则会残留指向上一个标注的回调，导致选中其他实体时按删除键误删旧标注
    if (!selected || (selected.type !== EEntityType.AreaDimension && selected.type !== EEntityType.LinearDimension)) {
      this._keyboardController.unregister(EShortcutId.REMOVE_ENTITY);
      return;
    }

    const { entityId, type } = selected;
    this._keyboardController.register({
      id: EShortcutId.REMOVE_ENTITY,
      callback: () =>
        type === EEntityType.AreaDimension
          ? globalStores.dimensionOperation.deleteAreaDimension(entityId)
          : globalStores.dimensionOperation.deleteLinearDimension(entityId)
    });
  }

  on = <K extends keyof EditorEventType>(eventName: K, callback: EditorEventType[K]) => {
    this._eventBus.on(eventName, callback);
  };

  off = <K extends keyof EditorEventType>(eventName: K, callback: EditorEventType[K]) => {
    this._eventBus.off(eventName, callback);
  };

  startAnimate() {
    // 获取场景和相机
    const renderer = globalStores.editorStateStore.sceneInfo.renderer;
    const sceneInfo =
      this.sceneMode === ESceneMode.scene2d
        ? globalStores.editorStateStore.sceneInfo.scene2d
        : globalStores.editorStateStore.sceneInfo.scene3d;

    const animate = () => {
      this._controllerManager.keyboardMoveUpdate();

      renderer.render(sceneInfo.scene, sceneInfo.camera);
      this._requestAnimationFrameId = requestAnimationFrame(animate);
    };
    animate();
  }

  stopAnimate() {
    if (this._requestAnimationFrameId) {
      cancelAnimationFrame(this._requestAnimationFrameId);
      this._requestAnimationFrameId = null;
    }
  }

  destroy() {
    this.stopAnimate();
    this.contextMenuController.destroy();
    this._groundModel2d.dispose();
    this._groundModel3d.dispose();
    this._keyboardController.destroy();
  }
}
